import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { CompanyStore } from '../../features/company-context/store/company.store';
import {
  normalizeCompanyMembershipRole,
  normalizeCompanyRoleFromClaim,
} from '../../features/company-context/utils/company-membership-role.util';
import { AuthService } from '../services/auth.service';
import { SessionRoleSyncService } from '../services/session-role-sync.service';
import { CompanyScopeResetService } from './company-scope-reset.service';
import { isStrictCompanyAdminWorkspaceAccess } from '../utils/session-company-admin-workspace.util';
import { pickFirstCompanyRoleValueFromRecords } from '../utils/taskeo-company-role-response.util';
import {
  decodeJwtPayloadJson,
  jwtScopedCompanyIdMatchesActive,
  readCompanyRoleRawFromJwtPayload,
  readScopedCompanyIdFromJwtPayload,
} from '../utils/jwt-access-token-claims.util';

const LOCAL_AUTH_USER_STORAGE_KEY = 'taskeo.auth.user';

@Injectable({ providedIn: 'root' })
export class CompanyContextService {
  private readonly companyStore = inject(CompanyStore);
  private readonly scopeReset = inject(CompanyScopeResetService);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly sessionRoleSync = inject(SessionRoleSyncService);

  /** Reavalia `isAdmin` após cada `NavigationEnd` (URL vs chave da empresa na sessão). */
  private readonly adminCheckNavTick = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => Date.now()),
      startWith(0)
    ),
    { initialValue: 0 }
  );

  readonly loadingCompanies = this.companyStore.loading;
  readonly companies = computed(() =>
    this.companyStore.memberships().map((membership) => ({
      id: membership.companyId,
      companyKey: membership.companyKey,
      name: membership.companyName,
      slug: membership.companyName,
      role: membership.role,
      logoUrl: membership.companyLogo ?? undefined,
      language: null,
      vertical: membership.vertical,
    }))
  );
  readonly activeCompany = computed(() => {
    const active = this.companyStore.activeCompany();
    if (!active) return null;
    return {
      id: active.companyId,
      companyKey: active.companyKey,
      name: active.companyName,
      slug: active.companyName,
      role: active.role,
      logoUrl: active.companyLogo ?? undefined,
      language: null,
      vertical: active.vertical,
    };
  });
  readonly activeCompanyId = this.companyStore.activeCompanyId;
  readonly activeCompanyKey = this.companyStore.activeCompanyKey;
  /**
   * Gestão operacional na empresa (membros de projeto/processo, partilhas, etc.): **Admin ou Owner**
   * no enum da API. **Member** não.
   *
   * Regra de negócio Taskeo: quem **cria** a empresa é **`UserRole.Admin` (2)**, não Owner. `Owner` (1)
   * existe no enum para fluxos explícitos (ex. “fundador” com permissões à parte); não é o papel por
   * defeito do criador. Ordem de resolução: membership → JWT → sessão, com critérios de mesma empresa.
   */
  readonly isAdmin = computed(() => {
    void this.adminCheckNavTick();
    const activeId = this.companyStore.activeCompanyId()?.trim();
    const urlMatch = this.router.url.match(/\/c\/([^/?#]+)/i);
    const routeKey = urlMatch?.[1] ? decodeURIComponent(urlMatch[1]).trim().toLowerCase() : '';
    /** Inclui slug derivado do nome quando a API não manda `companyKey`. */
    const resolvedActiveKey = this.companyStore.activeCompanyKey()?.trim().toLowerCase() ?? '';

    if (activeId) {
      for (const m of this.companyStore.memberships()) {
        if (!this.companyIdsLooselyEqual(m.companyId, activeId)) continue;
        const r = normalizeCompanyMembershipRole(m.role, 'Member');
        if (r === 'Admin' || r === 'Owner') return true;
      }
    }

    const membership = this.companyStore.activeCompany();
    const fromMembership = normalizeCompanyMembershipRole(membership?.role, 'Member');
    if (fromMembership === 'Admin' || fromMembership === 'Owner') return true;

    const token = this.auth.getToken();
    if (token) {
      const payload = decodeJwtPayloadJson(token);
      if (payload) {
        const jwtCid = readScopedCompanyIdFromJwtPayload(payload);
        const jwtTenantAligned =
          !activeId ||
          jwtCid === undefined ||
          jwtScopedCompanyIdMatchesActive(jwtCid, activeId);
        if (jwtTenantAligned) {
          const fromJwt = normalizeCompanyRoleFromClaim(
            readCompanyRoleRawFromJwtPayload(payload),
            'Member'
          );
          if (fromJwt === 'Admin' || fromJwt === 'Owner') return true;
        }
      }
    }

    const session = this.auth.currentUser();
    if (!session) return false;

    const fromSession = normalizeCompanyMembershipRole(session.role, 'Member');
    if (fromSession !== 'Admin' && fromSession !== 'Owner') return false;

    const sessionCompanyId = session.companyId?.trim();
    const activeKey = this.companyStore.activeCompanyKey()?.trim().toLowerCase();
    const sessionKey = session.companyKey?.trim().toLowerCase();

    const sameCompanyById = !!(activeId && sessionCompanyId && activeId === sessionCompanyId);
    const sameCompanyByKey = !!(activeKey && sessionKey && activeKey === sessionKey);
    const sameCompanyByRouteKey = !!(activeKey && routeKey && activeKey === routeKey);
    const sessionRouteKeyMatch = !!(routeKey && sessionKey && sessionKey === routeKey);
    const viewingActiveCompanyByUrl =
      !!(
        activeId &&
        membership &&
        membership.companyId.trim() === activeId &&
        routeKey &&
        resolvedActiveKey &&
        resolvedActiveKey === routeKey
      );

    if (
      sameCompanyById ||
      sameCompanyByKey ||
      sameCompanyByRouteKey ||
      sessionRouteKeyMatch ||
      viewingActiveCompanyByUrl
    ) {
      return true;
    }

    const loc = this.readLocalAuthUserRecord();
    const locCid = typeof loc['companyId'] === 'string' ? loc['companyId'].trim() : '';
    if (activeId && locCid && this.companyIdsLooselyEqual(locCid, activeId)) {
      const rawRv = pickFirstCompanyRoleValueFromRecords([loc]);
      const rawRole = rawRv ?? loc['role'] ?? loc['Role'];
      const lr = normalizeCompanyMembershipRole(rawRole, 'Member');
      if (lr === 'Admin' || lr === 'Owner') return true;
    }

    if (session && activeId && routeKey) {
      const sr = normalizeCompanyMembershipRole(session.role, 'Member');
      if (sr === 'Admin' || sr === 'Owner') {
        const nameSlug = this.slugifyPublicCompanyKey(session.companyName ?? '');
        if (nameSlug && nameSlug === routeKey) return true;
      }
    }

    return false;
  });

  /**
   * Proprietário da empresa ativa (gestão de papéis em projeto/processos; distinto de Admin).
   * Usa membership e, se o companyId da sessão coincidir, o papel guardado no login.
   */
  readonly isCompanyOwner = computed(() => {
    void this.adminCheckNavTick();
    const m = normalizeCompanyMembershipRole(this.companyStore.activeCompany()?.role, 'Member');
    if (m === 'Owner') return true;
    const session = this.auth.currentUser();
    if (!session) return false;
    const activeId = this.companyStore.activeCompanyId()?.trim();
    const sid = session.companyId?.trim();
    const activeKey = this.companyStore.activeCompanyKey()?.trim().toLowerCase();
    const sessionKey = session.companyKey?.trim().toLowerCase();
    const sameById = !!(activeId && sid && activeId === sid);
    const sameByKey = !!(activeKey && sessionKey && activeKey === sessionKey);
    const urlMatch = this.router.url.match(/\/c\/([^/?#]+)/i);
    const routeKey = urlMatch?.[1] ? decodeURIComponent(urlMatch[1]).trim().toLowerCase() : '';
    const sessionRouteKeyMatch = !!(routeKey && sessionKey && sessionKey === routeKey);
    if (!sameById && !sameByKey && !sessionRouteKeyMatch) return false;
    return normalizeCompanyMembershipRole(session.role, 'Member') === 'Owner';
  });

  /**
   * Menu «dados da empresa» / preferências: **só Admin (2)** — sessão, JWT, login;
   * fallback: membership/tenant da empresa ativa (só `Admin`, não Owner).
   */
  readonly canAccessCompanyWorkspaceMenu = computed(() => {
    void this.auth.currentUser();
    void this.auth.token();
    void this.companyStore.activeCompany();
    if (isStrictCompanyAdminWorkspaceAccess(this.auth.currentUser(), this.auth.getToken())) return true;
    const active =
      this.companyStore.activeCompanyId()?.trim() || this.auth.currentUser()?.companyId?.trim() || '';
    if (!active) return false;
    const m = this.companyStore.activeCompany();
    return normalizeCompanyMembershipRole(m?.role, 'Member') === 'Admin';
  });

  async initCompanyContext(): Promise<void> {
    await this.companyStore.loadMemberships();
    try {
      await this.sessionRoleSync.syncSessionRoleFromMeEndpoints();
    } catch {
      // opcional: não bloquear shell se /me falhar
    }
    // Hidrata vertical da Company ativa via GET /api/companies (silencioso em erro/DTO antigo).
    void this.companyStore.hydrateActiveCompanyVertical();
  }

  async switchCompany(companyId: string): Promise<void> {
    this.scopeReset.resetForCompanyChange();
    this.companyStore.setActiveCompany(companyId);
    const membership = this.companyStore.memberships().find((m) => m.companyId === companyId);
    const session = this.auth.currentUser();
    if (session && membership) {
      const role = normalizeCompanyMembershipRole(
        membership.role,
        normalizeCompanyMembershipRole(session.role, 'Member')
      );
      this.auth.setCurrentUser({
        ...session,
        companyId: membership.companyId,
        companyKey: membership.companyKey?.trim() || session.companyKey,
        companyName: membership.companyName,
        role,
        roleValue: undefined,
      });
    }
    this.auth.applyRoleFromCurrentAccessToken();
    void this.sessionRoleSync.syncSessionRoleFromMeEndpoints();
    // Re-hidrata vertical da nova Company ativa em background.
    void this.companyStore.hydrateActiveCompanyVertical();
    const key = this.companyStore.activeCompanyKey();
    if (!key) return;
    const currentUrl = this.router.url;
    const targetUrl = currentUrl.replace(/\/c\/[^\/?#]+/i, `/c/${key}`);
    if (targetUrl !== currentUrl && targetUrl.startsWith('/c/')) {
      await this.router.navigateByUrl(targetUrl);
      return;
    }
    await this.router.navigate(['/c', key, 'dashboard']);
  }

  async resolveByKey(companyKey: string) {
    return this.companyStore.resolveFromRouteKey(companyKey);
  }

  clearCurrentCompany(): void {
    this.resetContext();
  }

  setCurrentCompany(companyId: string | null): void {
    this.companyStore.setActiveCompany(companyId);
  }

  /**
   * Após salvar Definições > Geral / dados da empresa: propaga novo logo/tradeName
   * pelo store, tenant **e sessão**. Sem o sync da sessão, o switcher pode renderizar
   * o nome antigo via `auth.currentUser().companyName` — única fonte que sobrevive
   * a re-renderizações disparadas por interceptors / fallbacks de session.
   *
   * O `companyKey` é opaco (não derivado do nome) e não muda no rename — nada a
   * regerar nem renavegar.
   */
  applyActiveCompanyProfileUpdate(
    companyId: string,
    patch: { logoUrl: string | null; tradeName?: string }
  ): void {
    const tradeName = patch.tradeName?.trim();

    this.companyStore.applyCompanyProfileFromSettings(companyId, {
      logoUrl: patch.logoUrl,
      tradeName: patch.tradeName,
    });

    const session = this.auth.currentUser();
    if (!session) return;
    const sessionCompanyId = session.companyId?.trim();
    const targetId = companyId.trim();
    if (!sessionCompanyId || !targetId) return;
    if (!this.companyIdsLooselyEqual(sessionCompanyId, targetId)) return;

    if (!!tradeName && tradeName !== session.companyName) {
      this.auth.setCurrentUser({
        ...session,
        companyName: tradeName,
      });
    }
  }

  resetContext(): void {
    this.scopeReset.resetForCompanyChange();
    this.companyStore.resetContext();
  }

  /** GUID com ou sem hífenes / casing. */
  private companyIdsLooselyEqual(a: string, b: string): boolean {
    const x = a?.trim().replace(/-/g, '').toLowerCase() ?? '';
    const y = b?.trim().replace(/-/g, '').toLowerCase() ?? '';
    return x.length > 0 && x === y;
  }

  private readLocalAuthUserRecord(): Record<string, unknown> {
    try {
      const raw = localStorage.getItem(LOCAL_AUTH_USER_STORAGE_KEY);
      if (!raw) return {};
      const o = JSON.parse(raw) as unknown;
      return o && typeof o === 'object' && !Array.isArray(o) ? (o as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }

  private slugifyPublicCompanyKey(value: string): string {
    return value
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }
}
